import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Api } from '../../application/panelApi';
import { confirmDialog } from '../../components/ModalHost';
import { toast } from '../../components/ToastHost';
import { errText, nf, plural } from '../../lib/format';
import { useApp } from '../../store/AppContext';
import {
  ENGINES,
  PHASES,
  PLATFORMS,
  STATUSES,
  type DeleteJob,
  type DeletePreview,
  type DeleteRow,
  type NodeCluster,
} from './model';
import { Chips } from './shared';

type DeleteFilter = Record<string, unknown>;

type BulkDeleteTabProps = {
  active: boolean;

  clusters: NodeCluster[];
  engineChromium: number;
};

const profilesWord = (n: number) => plural(n, 'профиль', 'профиля', 'профилей');

export function BulkDeleteTab({ active, clusters, engineChromium }: BulkDeleteTabProps) {
  const { ui } = useApp();
  const [clusterSel, setClusterSel] = useState('all');
  const [phases, setPhases] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [engines, setEngines] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [older, setOlder] = useState('');
  const [newer, setNewer] = useState('');
  const [idle, setIdle] = useState('');
  const [maxDays, setMaxDays] = useState('');
  const [never, setNever] = useState(false);
  const [chromeOp, setChromeOp] = useState('');
  const [chromeMajor, setChromeMajor] = useState('');
  const [limit, setLimit] = useState('');

  const [rows, setRows] = useState<Record<string, DeleteRow>>({});
  const [counting, setCounting] = useState(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;


  const cluster = clusterSel === 'all' || clusters.some((c) => c.id === clusterSel) ? clusterSel : 'all';
  const clusterName = (id: string) => clusters.find((c) => c.id === id)?.name || id;

  const readFilter = (): DeleteFilter => {
    const f: DeleteFilter = {};
    const num = (v: string): number | undefined => {
      const s = v.trim();
      return s === '' ? undefined : Number(s);
    };
    if (phases.length) f.phases = phases;
    if (statuses.length) f.statuses = statuses;
    if (engines.length) f.searchEngines = engines;
    if (platforms.length) f.platforms = platforms;
    if (num(older)) f.createdOlderThanDays = num(older);
    if (num(newer)) f.createdNewerThanDays = num(newer);
    if (num(idle)) f.lastRunOlderThanDays = num(idle);
    if (num(maxDays) !== undefined) f.maxFarmDays = num(maxDays);
    if (never) f.neverRun = true;
    if (chromeOp) f.chromium = { op: chromeOp, major: num(chromeMajor) ?? engineChromium };
    if (num(limit)) f.limit = num(limit);
    return f;
  };

  const targets = (): string[] => (cluster === 'all' ? clusters.map((c) => c.id) : cluster ? [cluster] : []);

  const previewDelete = async (): Promise<{ filter: DeleteFilter; rows: Record<string, DeleteRow> } | undefined> => {
    const ids = targets();
    if (!ids.length) {
      toast('Нет доступных нод. Добавьте или включите сервер в разделе «Сервера».', 'err');
      return undefined;
    }
    const filter = readFilter();
    if (!Object.keys(filter).some((k) => k !== 'limit')) {
      toast('Не задано ни одного условия. Выберите хотя бы одно, чтобы случайно не удалить все профили.', 'err');
      return undefined;
    }
    setRows({});
    setCounting(true);
    const next: Record<string, DeleteRow> = {};
    await Promise.all(
      ids.map(async (cid) => {
        try {
          const r = (await Api.clusters.profilesDelete(cid, filter, true)) as { preview: DeletePreview };
          next[cid] = { preview: r.preview };
        } catch (err) {
          next[cid] = { error: errText(err) };
        }
      }),
    );
    setCounting(false);
    setRows(next);
    return { filter, rows: next };
  };

  const runDelete = async () => {
    const res = await previewDelete();
    if (!res) return;
    const { filter, rows: counted } = res;
    const total = Object.values(counted).reduce((n, r) => n + (r.preview?.count ?? 0), 0);
    if (!total) {
      toast('Ни один профиль не подходит под заданные условия, удалять нечего. Измените условия и попробуйте снова.');
      return;
    }
    confirmDialog(
      'Удалить профили безвозвратно?',
      <>
        Будет удалено <b>{nf.format(total)}</b> {profilesWord(total)} вместе с куками, историей и отпечатком.
        Восстановить их будет нельзя. Профили, запущенные в этот момент, будут пропущены.
      </>,
      () => {
        void (async () => {
          const next: Record<string, DeleteRow> = { ...counted };
          await Promise.all(
            Object.keys(counted).map(async (cid) => {
              if (!counted[cid]?.preview?.count) return;
              try {
                const r = (await Api.clusters.profilesDelete(cid, filter, false)) as { job: DeleteJob };
                next[cid] = { ...next[cid], job: r.job };
              } catch (err) {
                next[cid] = { ...next[cid], error: errText(err) };
              }
            }),
          );
          setRows(next);
        })();
      },
      { word: 'УДАЛИТЬ', yes: 'Удалить' },
    );
  };

  const reset = () => {
    setPhases([]);
    setStatuses([]);
    setEngines([]);
    setPlatforms([]);
    setOlder('');
    setNewer('');
    setIdle('');
    setMaxDays('');
    setChromeMajor('');
    setLimit('');
    setNever(false);
    setChromeOp('');
    setRows({});
    setCounting(false);
  };


  const anyRunning = Object.values(rows).some((r) => r.job?.status === 'running');
  useEffect(() => {
    if (!anyRunning) return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      void (async () => {
        const current = rowsRef.current;
        const running = Object.keys(current).filter((cid) => current[cid]?.job?.status === 'running');
        const jobs = await Promise.all(
          running.map(async (cid): Promise<[string, DeleteJob | null]> => {
            try {
              const { job } = (await Api.clusters.profilesDeleteState(cid)) as { job?: DeleteJob | null };
              return [cid, job ?? null];
            } catch {
              return [cid, null];
            }
          }),
        );
        if (!alive) return;
        const next: Record<string, DeleteRow> = { ...rowsRef.current };
        for (const [cid, job] of jobs) {
          const row = next[cid];
          if (job && row) next[cid] = { ...row, job };
        }
        setRows(next);
        if (!Object.values(next).some((r) => r.job?.status === 'running')) toast('Удаление завершено. Итоги по каждой ноде — в таблице «Под удаление».', 'ok');
      })();
    }, 2000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [anyRunning, rows]);

  const ids = Object.keys(rows);
  let total = 0;
  let body: ReactNode;
  if (counting) {
    body = (
      <tr>
        <td colSpan={5}>
          <div className="empty">Считаем профили, которые подходят под условия…</div>
        </td>
      </tr>
    );
  } else if (!ids.length) {
    body = (
      <tr>
        <td colSpan={5}>
          <div className="empty">Подсчёт не выполнялся.</div>
        </td>
      </tr>
    );
  } else {
    body = ids.map((cid) => {
      const r = rows[cid] ?? {};
      if (r.error) {
        return (
          <tr key={cid}>
            <td>
              <b>{clusterName(cid)}</b>
            </td>
            <td colSpan={4} className="pr-res bad">
              {r.error}
            </td>
          </tr>
        );
      }
      const p = r.preview;
      const j = r.job;
      total += p?.count ?? 0;
      const byPhase = p ? Object.entries(p.byPhase || {}).map(([k, v]) => `${k}: ${nf.format(Number(v))}`).join(', ') : '';
      const state = j
        ? j.status === 'running'
          ? `удаление… ${nf.format(j.deleted)} / ${nf.format(j.total)}`
          : `${j.status === 'done' ? 'удалено' : 'ошибка'} ${nf.format(j.deleted)}${j.failed ? `, ошибок ${j.failed}` : ''}${
              j.skippedRunning ? `, пропущено запущенных ${j.skippedRunning}` : ''
            }`
        : p?.skippedRunning
          ? `${p.skippedRunning} запущенных будут пропущены`
          : '';
      return (
        <tr key={cid}>
          <td>
            <b>{clusterName(cid)}</b>
            {p?.sample?.length ? (
              <div className="subnum mono">
                {p.sample.slice(0, 5).join(', ')}
                {p.count > 5 ? '…' : ''}
              </div>
            ) : null}
          </td>
          <td className="num">{p ? nf.format(p.matched) : '—'}</td>
          <td className="num">
            <b>{p ? nf.format(p.count) : '—'}</b>
          </td>
          <td className="pr-res">{byPhase || '—'}</td>
          <td className="pr-res">{state}</td>
        </tr>
      );
    });
  }
  const totalText = !counting && ids.length ? `${nf.format(total)} ${profilesWord(total)}` : '';

  const needsMajor = ['lt', 'eq', 'gt'].includes(chromeOp);

  return (
    <div className={`pr-pane${active ? '' : ' hidden'}`} data-prpane="delete">
      <div className="grid-warm pr-grid">
        <div className="sec">
          <h2>
            <span className="sq" />
            Массовое удаление профилей
          </h2>
          <div className="field-row">
            <label htmlFor="bdCluster">Ноды</label>
            <select id="bdCluster" value={cluster} onChange={(e) => setClusterSel(e.target.value)}>
              <option value="all">Все ноды</option>
              {clusters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field-row">
            <label>Фаза</label>
            <Chips id="bdPhases" name="phase" items={PHASES} selected={phases} onChange={setPhases} />
          </div>
          <div className="field-row">
            <label>Статус</label>
            <Chips id="bdStatuses" name="status" items={STATUSES} selected={statuses} onChange={setStatuses} />
          </div>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="bdOlder">Созданы более N дней назад</label>
              <input type="number" id="bdOlder" min={0} step={0.5} placeholder="Пусто — не учитывать" value={older} onChange={(e) => setOlder(e.target.value)} />
            </div>
            <div className="field-row">
              <label htmlFor="bdNewer">Созданы менее N дней назад</label>
              <input type="number" id="bdNewer" min={0} step={0.5} placeholder="Пусто — не учитывать" value={newer} onChange={(e) => setNewer(e.target.value)} />
            </div>
          </div>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="bdIdle">Не запускались N дней</label>
              <input type="number" id="bdIdle" min={0} step={0.5} placeholder="Пусто — не учитывать" value={idle} onChange={(e) => setIdle(e.target.value)} />
            </div>
            <div className="field-row">
              <label htmlFor="bdMaxDays">Дней прогрева не больше</label>
              <input type="number" id="bdMaxDays" min={0} placeholder="Пусто — не учитывать" value={maxDays} onChange={(e) => setMaxDays(e.target.value)} />
            </div>
          </div>
          <div className="set-row">
            <div className="txt">
              <b>Ни разу не запускались</b>
              <small>Профиль создан, но браузер с ним ещё ни разу не открывался.</small>
            </div>
            <label className="switch">
              <input type="checkbox" id="bdNever" checked={never} onChange={(e) => setNever(e.target.checked)} />
              <i />
            </label>
          </div>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="bdChromeOp">Версия Chromium профиля</label>
              <select
                id="bdChromeOp"
                value={chromeOp}
                onChange={(e) => {
                  const op = e.target.value;
                  setChromeOp(op);
                  if (['lt', 'eq', 'gt'].includes(op) && !chromeMajor && engineChromium) {
                    setChromeMajor(String(engineChromium));
                  }
                }}
              >
                <option value="">Любая</option>
                <option value="incompatible">Новее движка (не откроются)</option>
                <option value="lt">Старше чем</option>
                <option value="eq">Равна</option>
                <option value="gt">Новее чем</option>
                <option value="unknown">Неизвестна (не запускались)</option>
              </select>
            </div>
            <div className="field-row">
              <label htmlFor="bdChromeMajor">Мажор</label>
              <input
                type="number"
                id="bdChromeMajor"
                min={1}
                placeholder={engineChromium ? `Например: ${engineChromium}` : 'Например: 150'}
                disabled={!needsMajor}
                value={chromeMajor}
                onChange={(e) => setChromeMajor(e.target.value)}
              />
            </div>
          </div>
          <div className="field-row">
            <label>Поисковик</label>
            <Chips
              id="bdEngines"
              name="engine"
              items={ENGINES.filter(([id]) => id === ui.scope || id === 'none')}
              selected={engines}
              onChange={setEngines}
            />
          </div>
          <div className="field-row">
            <label>ОС отпечатка</label>
            <Chips id="bdPlatforms" name="platform" items={PLATFORMS} selected={platforms} onChange={setPlatforms} />
          </div>
          <div className="field-row">
            <label htmlFor="bdLimit">Не больше N на ноду</label>
            <input type="number" id="bdLimit" min={1} placeholder="Пусто — без ограничения" value={limit} onChange={(e) => setLimit(e.target.value)} />
          </div>
          <div className="sec-foot">
            <button type="button" className="btn" id="bdReset" onClick={reset}>
              Сбросить
            </button>
            <button type="button" className="btn" id="bdPreview" onClick={() => void previewDelete()}>
              Посчитать
            </button>
            <button type="button" className="btn btn-danger" id="bdRun" onClick={() => void runDelete()}>
              Удалить
            </button>
          </div>
        </div>
        <div className="panel">
          <div className="panel-head">
            <h2>Под удаление</h2>
            <span className="count" id="bdTotal">
              {totalText}
            </span>
          </div>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Нода</th>
                  <th style={{ textAlign: 'right' }}>Подходит</th>
                  <th style={{ textAlign: 'right' }}>Удалим</th>
                  <th>По фазам</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody id="bdBody">{body}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
